import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Box,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import CustomTextField from "../../../../components/ui/Textfield";
import CustomSelect from "../../../../components/ui/Select";
import { tokens } from "../../../../theme";
import { Close } from "@mui/icons-material";
import apiService from "../../../../service/Api";
import { useParams } from "react-router-dom";
import type { IStreamDetails } from "../../../../types/streams";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";

const YOUTUBE_CATEGORIES = [
  { id: "29", label: "29 – Nonprofits & Activism" },
  { id: "22", label: "22 – People & Blogs" },
  { id: "19", label: "19 – Travel & Events" },
  { id: "25", label: "25 – News & Politics" },
  { id: "27", label: "27 – Education" },
  { id: "24", label: "24 – Entertainment" },
  { id: "1", label: "1 – Film & Animation" },
  { id: "2", label: "2 – Autos & Vehicles" },
  { id: "10", label: "10 – Music" },
  { id: "15", label: "15 – Pets & Animals" },
  { id: "17", label: "17 – Sports" },
  { id: "20", label: "20 – Gaming" },
  { id: "23", label: "23 – Comedy" },
  { id: "26", label: "26 – Howto & Style" },
  { id: "28", label: "28 – Science & Technology" },
];

const BACKLINK_BLOCK = `\n--------------------------------------------------\nWatch more live streams & services on our website:\n🌐 https://muzukuru.com`;

function stripBacklink(text: string) {
  return text.replace(/\n*--------------------------------------------------\nWatch more live streams & services on our website:\n🌐 https:\/\/muzukuru\.com$/, "").trimEnd();
}

type Playlist = { id: string; label: string };

interface IStreamData {
  streamTitle: string;
  description: string;
  privacySetting: "Public" | "Private" | "Unlisted";
  thumbnailUrl: string;
  scheduledStartTime: string;
}

export interface IGenerateYouTubeLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedChannel?: { name?: string } | null;
  streamData?: (Omit<IStreamDetails, "booking"> & {
    booking: IStreamDetails["booking"] & {
      funeral_home?: { name: string } | null;
    };
  }) | null;
  crewMembers?: Array<{
    crew_member: {
      user: {
        first_name: string;
        last_name: string;
      };
    };
    assignment?: {
      role?: string | null;
      notes?: string;
    }[];
  }>;
  thumbnailUrl?: string;
}

const YouTubeModal: React.FC<IGenerateYouTubeLinkDialogProps> = ({
  open,
  onClose,
  streamData: propStreamData,
  crewMembers = [],
  onSuccess,
  thumbnailUrl: propThumbnailUrl,
  selectedChannel,
}) => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<IStreamData>>({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [categoryId, setCategoryId] = useState("29");
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [playlistId, setPlaylistId] = useState("");
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [appendBacklink, setAppendBacklink] = useState(true);
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");

  const formatCrewDescription = useCallback((crew: typeof crewMembers) => {
    if (!crew?.length) {
      return "";
    }

    const roleOrder = [
      "Executive Producer",
      "Producer",
      "Assistant Producer",
      "Liveswitcher",
    ];

    const sortedCrew = [...crew].sort((a, b) => {
      const roleA = a.assignment?.[0]?.role || "Crew Member";
      const roleB = b.assignment?.[0]?.role || "Crew Member";

      const indexA = roleOrder.indexOf(roleA);
      const indexB = roleOrder.indexOf(roleB);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) {
        return -1;
      }

      if (indexB !== -1) {
        return 1;
      }
      return 0;
    });

    return sortedCrew
      .map((member) => {
        const fullName = `${member.crew_member.user.first_name} ${member.crew_member.user.last_name}`;
        const role = member.assignment?.[0]?.role || "Crew Member";
        return `${role} - ${fullName}`;
      })
      .join("\n");
  }, []);
  const formatStreamTitle = useCallback(
    (streamData: typeof propStreamData) =>
      streamData?.booking?.deceased?.[0]
        ? `Celebrating the life of ${streamData.booking.deceased[0].first_name} ${streamData.booking.deceased[0].surname}`
        : streamData?.title || "Live Stream",
    []
  );

  const formatDescription = useCallback(
    (
      streamData: typeof propStreamData,
      crew: typeof crewMembers,
      selectedChannel?: { name?: string } | null
    ) => {
    const channelName = selectedChannel?.name || "";

    const venueName =
      streamData?.venue_name && streamData.venue_name !== "N/A"
        ? streamData.venue_name
        : streamData?.service?.venue?.name &&
            streamData.service.venue.name !== "N/A"
          ? streamData.service.venue.name
          : streamData?.event_details?.venue?.name || "Venue TBC";

    const address = [
      streamData?.address_one,
      streamData?.address_two,
      streamData?.suburb,
      streamData?.city,
    ]
      .filter((part) => part && part !== "N/A")
      .join(", ");

    const funeralHomeName = streamData?.booking?.funeral_home?.name;
    const serviceLine = funeralHomeName ? `Service by ${funeralHomeName}` : "";

    return `${channelName}
Broadcasting live from ${venueName}, ${address}
${serviceLine}
${formatCrewDescription(crew)}`;
  },
  [formatCrewDescription]
  );

  const formatScheduledTime = useCallback(
    (streamData: typeof propStreamData) =>
      streamData?.date && streamData?.start
        ? `${streamData.date}T${streamData.start}`.slice(0, 16)
        : new Date().toISOString().slice(0, 16),
    []
  );

  const [streamData, setStreamData] = useState<IStreamData>({
    streamTitle: "",
    description: "",
    thumbnailUrl: propThumbnailUrl || "",
    scheduledStartTime: "",
    privacySetting: "Public",
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>(
    propThumbnailUrl || ""
  );

  useEffect(() => {
    if (propStreamData) {
      setStreamData({
        streamTitle: formatStreamTitle(propStreamData),
        description: formatDescription(propStreamData, crewMembers),
        thumbnailUrl: propThumbnailUrl || "",
        scheduledStartTime: formatScheduledTime(propStreamData),
        privacySetting: "Public",
      });
      setThumbnailPreview(propThumbnailUrl || "");
      setErrors({});
      setIsUpdate(!!propStreamData.youtube_stream_link);
      setCategoryId(propStreamData.youtube_stream_data?.category_id || "29");
      setPlaylistId(propStreamData.youtube_stream_data?.playlist_id || "");
      setAppendBacklink(propStreamData.youtube_stream_data?.append_backlinks ?? true);
    }
  }, [
    crewMembers,
    formatDescription,
    formatScheduledTime,
    formatStreamTitle,
    propStreamData,
    propThumbnailUrl,
  ]);

  useEffect(() => {
    if (!open || !id) return;
    let cancelled = false;
    setPlaylistsLoading(true);
    apiService
      .get<{ results: Array<{ id: string; title: string }> }>(
        `/api/streams/stream/${id}/youtube-playlists/`
      )
      .then((response) => {
        if (cancelled) return;
        const loadedPlaylists = response.data.results.map(({ id: playlistKey, title }) => ({
          id: playlistKey,
          label: title,
        }));
        setPlaylists(loadedPlaylists);
        setPlaylistId((current) => {
          if (current && loadedPlaylists.some((playlist) => playlist.id === current)) return current;
          const preferred = loadedPlaylists.find((playlist) => playlist.label.toLowerCase() === "catholic churches");
          return preferred?.id || loadedPlaylists[0]?.id || "";
        });
      })
      .catch(() => {
        if (!cancelled) showErrorToast("Could not load playlists from the connected YouTube channel.", "youtube-playlists-error");
      })
      .finally(() => {
        if (!cancelled) setPlaylistsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setStreamData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputChange = (field: string, value: string) => {
    setStreamData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!["image/png", "image/jpeg"].includes(file.type))
        return showErrorToast("Only PNG or JPEG files allowed", "thumb-type");
      if (file.size > 2 * 1024 * 1024)
        return showErrorToast("Thumbnail must be under 2MB", "thumb-size");
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<IStreamData> = {};
    if (!streamData.streamTitle)
      newErrors.streamTitle = "Stream title required";
    if (!streamData.description) newErrors.description = "Description required";
    if (!streamData.scheduledStartTime)
      newErrors.scheduledStartTime = "Start time required";
    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return showErrorToast("Fill all fields", "form-error");
    setLoading(true);

    const formData = new FormData();
    formData.append("title", streamData.streamTitle);
    const description = stripBacklink(streamData.description);
    formData.append(
      "description",
      appendBacklink ? `${description}${BACKLINK_BLOCK}` : description
    );
    formData.append("category_id", categoryId);
    formData.append("playlist_id", playlistId);
    formData.append("append_backlinks", String(appendBacklink));
    formData.append("privacy_status", streamData.privacySetting.toLowerCase());
    formData.append("resolution", "720p");
    formData.append("frame_rate", "30fps");
    formData.append(
      "scheduled_start_time",
      `${streamData.scheduledStartTime}:00Z`
    );
    if (thumbnailFile)
      formData.append("thumbnail", thumbnailFile, thumbnailFile.name);
    else if (streamData.thumbnailUrl)
      formData.append("thumbnail_url", streamData.thumbnailUrl);

    try {
      const endpoint = isUpdate
        ? `/api/streams/stream/${id}/update-youtube-stream/`
        : `/api/streams/stream/${id}/create-youtube-stream/`;

      const response = isUpdate
        ? await apiService.put<{ playlist_warning?: string }>(endpoint, formData)
        : await apiService.post<{ playlist_warning?: string }>(endpoint, formData);

      if (response.data.playlist_warning) {
        showErrorToast(response.data.playlist_warning, "youtube-playlist-warning");
      }

      showSuccessToast(
        isUpdate
          ? "YouTube link updated successfully!"
          : "YouTube link generated successfully!"
      );
      onSuccess();
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { detail?: string; error?: string } } })
          ?.response?.data?.detail ||
        (error as { response?: { data?: { detail?: string; error?: string } } })
          ?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Something went wrong.";
      showErrorToast(errorMessage, "youtube-link-error");
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const savePlaylist = async () => {
    const label = newPlaylistName.trim();
    if (!label) return;
    if (!id) return showErrorToast("Stream ID is missing.", "youtube-playlist-error");

    setPlaylistsLoading(true);
    try {
      const response = await apiService.post<{ id: string; title: string }>(
        `/api/streams/stream/${id}/create-youtube-playlist/`,
        { title: label }
      );
      const createdPlaylist = { id: response.data.id, label: response.data.title };
      setPlaylists((current) => [
        ...current.filter((playlist) => playlist.id !== createdPlaylist.id),
        createdPlaylist,
      ]);
      setPlaylistId(createdPlaylist.id);
      setNewPlaylistName("");
      setIsCreatingPlaylist(false);
    } catch {
      showErrorToast("Could not create a playlist on the connected YouTube channel.", "youtube-playlist-error");
    } finally {
      setPlaylistsLoading(false);
    }
  };

  const cancelPlaylistCreation = () => {
    setNewPlaylistName("");
    setIsCreatingPlaylist(false);
  };

  const backlinkPreview = `${stripBacklink(streamData.description)}${BACKLINK_BLOCK}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
      >
        <Box display="flex" justifyContent="space-between">
          <DialogTitle sx={{ color: colors.primary[300] }}>
            {isUpdate ? "Update YouTube Link" : "Generate YouTube Link"}
            {selectedChannel && (
              <Typography sx={{ display: "block", color: colors.primary[100] }}>
                Channel: {selectedChannel.name}
              </Typography>
            )}
          </DialogTitle>
          <IconButton onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <CustomTextField
            label="Stream Title"
            name="streamTitle"
            value={streamData.streamTitle}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={loading}
            error={!!errors.streamTitle}
            helperText={errors.streamTitle}
          />
          <CustomTextField
            label="Description"
            name="description"
            value={streamData.description}
            onChange={handleChange}
            multiline
            rows={8}
            fullWidth
            margin="normal"
            required
            disabled={loading}
            error={!!errors.description}
            helperText={errors.description}
            sx={{
              "& .MuiInputBase-root": { height: "auto", minHeight: "120px" },
            }}
          />
          {appendBacklink && (
            <Box
              mt={1}
              p={2}
              border={`1px solid ${colors.primary[300]}`}
              borderRadius={1}
            >
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Description preview with backlink
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {backlinkPreview}
              </Typography>
            </Box>
          )}
          <Box my={2}>
            <Typography variant="subtitle1">Thumbnail</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleThumbnailChange}
              disabled={loading}
            />
            <Box
              mt={1}
              width={150}
              height={84}
              display="flex"
              alignItems="center"
              justifyContent="center"
              border={`1px solid ${colors.primary[300]}`}
              borderRadius={1}
            >
              {thumbnailPreview ? (
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Typography variant="caption">
                  {" "}
                  Manually Upload Thumbnail
                </Typography>
              )}
            </Box>
          </Box>
          <CustomTextField
            fullWidth
            type="datetime-local"
            label="Scheduled Start Time"
            value={streamData.scheduledStartTime}
            onChange={(e) =>
              setStreamData((prev) => ({
                ...prev,
                scheduledStartTime: e.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
            required
            disabled={loading}
            error={!!errors.scheduledStartTime}
            helperText={errors.scheduledStartTime}
          />
          <FormControl fullWidth margin="normal" required>
            <CustomSelect
              labelId="privacy-setting-label"
              id="privacy-setting"
              name="privacySetting"
              value={streamData.privacySetting}
              label="Privacy Setting"
              disabled={loading}
              onChange={(e) =>
                handleInputChange("privacySetting", e.target.value as string)
              }
            >
              <MenuItem value="Public">Public</MenuItem>
              <MenuItem value="Unlisted">Unlisted</MenuItem>
              <MenuItem value="Private">Private</MenuItem>
            </CustomSelect>
          </FormControl>
          <Box mt={3}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Typography
                sx={{
                  color: colors.primary[400],
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                Automation &amp; SEO
              </Typography>
              <Box flex={1} borderTop={`1px solid ${colors.primary[600]}`} />
            </Box>
            <Grid container spacing={2.25}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" mb={0.75}>
                  YouTube Category
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={categoryId}
                    onChange={(event) => setCategoryId(event.target.value)}
                    disabled={loading}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 480 },
                      },
                    }}
                    sx={{
                      height: 56,
                      borderRadius: 1.25,
                      bgcolor: theme.palette.background.paper,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.primary[300],
                      },
                    }}
                  >
                    {YOUTUBE_CATEGORIES.map((category) => (
                      <MenuItem
                        key={category.id}
                        value={category.id}
                        sx={{ minHeight: 30, py: 0.5 }}
                      >
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" mb={0.75}>
                  Target Playlist
                </Typography>
                {!playlistsLoading && playlists.length === 0 && (
                  <Alert severity="info" sx={{ mb: 1.25 }}>
                    No playlists found for the connected YouTube channel. Create one to continue.
                  </Alert>
                )}
                {isCreatingPlaylist ? (
                  <Box display="flex" gap={1.25} alignItems="stretch">
                    <TextField
                      fullWidth
                      autoFocus
                      placeholder="Enter new playlist name..."
                      value={newPlaylistName}
                      onChange={(event) => setNewPlaylistName(event.target.value)}
                      disabled={loading || playlistsLoading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 56,
                          borderRadius: 1.25,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={savePlaylist}
                      disabled={loading || playlistsLoading || !newPlaylistName.trim()}
                      sx={{
                        minWidth: 76,
                        height: 56,
                        borderRadius: 1.25,
                        bgcolor: colors.primary[500],
                        color: theme.palette.getContrastText(colors.primary[500]),
                        fontWeight: 700,
                        textTransform: "none",
                        "&:hover": { bgcolor: colors.primary[400] },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={cancelPlaylistCreation}
                      disabled={loading || playlistsLoading}
                      sx={{
                        minWidth: 92,
                        height: 56,
                        borderRadius: 1.25,
                        borderColor: colors.primary[300],
                        color: "text.secondary",
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" gap={1.25} alignItems="stretch">
                    <FormControl fullWidth>
                      <Select
                        value={playlistId}
                        onChange={(event) => setPlaylistId(event.target.value)}
                        disabled={loading || playlistsLoading}
                        sx={{
                          height: 56,
                          borderRadius: 1.25,
                          bgcolor: theme.palette.background.paper,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.primary[300],
                          },
                        }}
                      >
                        {!playlists.length && <MenuItem value="" disabled>No playlists available</MenuItem>}
                        {playlists.map((playlist) => (
                          <MenuItem key={playlist.id} value={playlist.id}>
                            {playlist.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={() => setIsCreatingPlaylist(true)}
                      disabled={loading || playlistsLoading}
                      sx={{
                        minWidth: 176,
                        height: 56,
                        borderRadius: 1.25,
                        borderColor: colors.primary[500],
                        color: colors.primary[400],
                        fontWeight: 700,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      + Create Playlist
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  px={2}
                  py={1.5}
                  border={`1px solid ${colors.primary[300]}55`}
                  borderRadius={1.25}
                  bgcolor={theme.palette.mode === "dark" ? colors.primary[900] : "#FBFBFC"}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Auto-append backlinks
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Adds muzukuru.com link block to description
                    </Typography>
                  </Box>
                  <Switch
                    checked={appendBacklink}
                    onChange={(event) => setAppendBacklink(event.target.checked)}
                    disabled={loading}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: colors.primary[500],
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: colors.primary[500],
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box display="flex" justifyContent="center" mt={3}>
            <motion.div
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  backgroundColor: colors.grey[300],
                  "&:hover": { backgroundColor: colors.grey[400] },
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isUpdate ? (
                  "UPDATE LINK"
                ) : (
                  "GENERATE LINK"
                )}
              </Button>
            </motion.div>
          </Box>
        </DialogContent>
      </motion.div>
    </Dialog>
  );
};

export default YouTubeModal;
